import { useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts'
import { supabase } from '../../supabase'
import { montant } from './format'
import { ORDER_TYPES, ORDER_STATUS } from './carnetUtils'
import {
  t, Stagger, Panel, PageHead, PageTitle, PageSub,
  StatGrid, Tile, ChipBar, Chip, Loading, Empty, Muted, TableWrap, Tableau,
} from './ui'

const COLORS_PROD = { Paille: '#C99A2E', Foin: '#0F5C4D', Luzerne: '#1E5E96' }
const SECTIONS = [
  { key: 'chiffres', label: '💰 Chiffres' },
  { key: 'clients', label: '👥 Clients' },
  { key: 'commandes', label: '🚛 Commandes' },
  { key: 'marges', label: '📈 Marges' },
]

const Gain = styled.span`
  font-weight: 650; font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$positive ? t.green : t.red)};
`

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
`
const ChartHead = styled.div`
  font-family: ${t.font}; font-size: 0.625rem; font-weight: 600;
  letter-spacing: 0.075em; text-transform: uppercase; color: ${t.textFaint};
  margin-bottom: 14px;
`
const BigNum = styled.div`
  font-family: ${t.serif}; font-size: 2.3rem; font-weight: 660;
  letter-spacing: -0.045em; line-height: 1; text-align: center;
  font-variant-numeric: tabular-nums;
  color: ${(p) => p.$c || t.text};
`
const BigSub = styled.div`
  font-family: ${t.font}; font-size: 0.78rem; color: ${t.textSoft};
  text-align: center; margin-top: 8px;
`
const Legend = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px 16px; justify-content: center; margin-top: 12px;
  span { display: flex; align-items: center; gap: 6px; font-family: ${t.font}; font-size: 0.76rem; color: ${t.textSoft}; }
  i { width: 9px; height: 9px; border-radius: 2px; display: inline-block; }
`
const RankRow = styled.div`
  display: flex; align-items: center; gap: 11px;
  padding: 9px 0;
  border-bottom: 1px solid ${t.lineSoft};
  font-family: ${t.font};
  &:last-child { border-bottom: none; }
`
const RankNum = styled.span`
  width: 19px; height: 19px; border-radius: ${t.rXs}; flex-shrink: 0;
  background: ${(p) => (p.$i === 0 ? t.gold : t.surfaceSunk)};
  color: ${(p) => (p.$i === 0 ? '#fff' : t.textFaint)};
  font-size: 0.635rem; font-weight: 620; font-variant-numeric: tabular-nums;
  display: flex; align-items: center; justify-content: center;
`
const RankName = styled.span`
  flex: 1; min-width: 0; font-size: 0.83rem; color: ${t.text}; font-weight: 520;
  letter-spacing: -0.012em;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`
const RankVal = styled.span`
  font-size: 0.825rem; font-weight: 600; letter-spacing: -0.018em;
  font-variant-numeric: tabular-nums; color: ${t.text}; white-space: nowrap;
`

const tipStyle = {
  fontFamily: t.font, fontSize: '0.765rem',
  background: t.surface,
  borderRadius: 8,
  border: `1px solid ${t.line}`,
  boxShadow: '0 4px 16px rgba(22,24,28,0.1)',
  color: t.text,
}
const tipItem = { color: t.text }
const tipLabel = { color: t.textFaint, fontSize: '0.7rem', marginBottom: 2 }
const axisTick = { fontSize: 10, fill: t.textFaint }

export default function StatistiquesAdmin() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [section, setSection] = useState('chiffres')

  useEffect(() => { fetchStats() }, [])

  const fetchStats = async () => {
    setLoading(true)
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()

    const { data: orders } = await supabase.from('orders')
      .select('id, quote_id, nb_camions_total, ca_total_chf, ca_reel_chf, poids_total_t, date_livraison, lignes, status, type, created_at, client_id, ca_compta_chf')
    const { data: clients } = await supabase.from('clients').select('id, type, canton, created_at')
    const { data: devis } = await supabase.from('quotes').select('id, status, created_at, validated_at')
    /* Le coût de revient vit sur le devis, figé au moment du calcul.
       On le rapatrie ici pour le confronter au chiffre d'affaires. */
    const { data: couts } = await supabase.from('quotes').select('id, cout_revient').not('cout_revient', 'is', null)
    const { data: noms } = await supabase.from('clients').select('id, name')
    /* Requête à part, et son échec est toléré : tant que la migration
       `orders.cout_revient` n'est pas jouée, cette colonne n'existe pas,
       et la demander dans la requête principale viderait toute la page
       au lieu du seul onglet des marges. */
    const { data: coutsCommandes } = await supabase.from('orders').select('id, cout_revient').not('cout_revient', 'is', null)

    if (!orders || !clients) { setLoading(false); return }

    const delivered = orders.filter((o) => o.status === 'delivered')

    // ── CA & volume sur 12 mois ──
    const months12 = []
    for (let i = 11; i >= 0; i--) {
      const d = new Date(currentYear, currentMonth - i, 1)
      const m = d.getMonth(); const y = d.getFullYear()
      const monthOrders = delivered.filter((o) => o.date_livraison &&
        new Date(o.date_livraison).getMonth() === m &&
        new Date(o.date_livraison).getFullYear() === y)
      months12.push({
        label: d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }),
        mois: d.toLocaleDateString('fr-FR', { month: 'short' }),
        ca: Math.round(monthOrders.reduce((s, o) => s + (o.ca_total_chf || 0), 0)),
        poids: Math.round(monthOrders.reduce((s, o) => s + (o.poids_total_t || 0), 0) * 10) / 10,
      })
    }

    const catOf = (produit) => produit?.toLowerCase().includes('luzerne') ? 'Luzerne'
      : (produit?.toLowerCase().includes('foin') || produit?.toLowerCase().includes('prairie')) ? 'Foin'
      : 'Paille'

    const prodMap = {}
    delivered.forEach((o) => {
      (o.lignes || []).forEach((l) => { prodMap[catOf(l.produit)] = (prodMap[catOf(l.produit)] || 0) + (l.sous_total || 0) })
    })
    const prodData = Object.entries(prodMap).map(([name, value]) => ({ name, value: Math.round(value) }))

    // ── Saisonnalité ──
    const MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
    const saisoData = MOIS.map((label, mi) => ({
      label,
      thisYear: Math.round(delivered.filter((o) => o.date_livraison &&
        new Date(o.date_livraison).getMonth() === mi &&
        new Date(o.date_livraison).getFullYear() === currentYear)
        .reduce((s, o) => s + (o.ca_total_chf || 0), 0)),
      lastYear: Math.round(delivered.filter((o) => o.date_livraison &&
        new Date(o.date_livraison).getMonth() === mi &&
        new Date(o.date_livraison).getFullYear() === currentYear - 1)
        .reduce((s, o) => s + (o.ca_total_chf || 0), 0)),
    }))

    // ── Clients ──
    const prospectsParMois = []
    for (let i = 5; i >= 0; i--) {
      const d = new Date(currentYear, currentMonth - i, 1)
      const m = d.getMonth(); const y = d.getFullYear()
      prospectsParMois.push({
        label: d.toLocaleDateString('fr-FR', { month: 'short' }),
        count: clients.filter((c) => new Date(c.created_at).getMonth() === m && new Date(c.created_at).getFullYear() === y).length,
      })
    }

    const fideles = clients.filter((c) => c.type === 'fidele').length
    const prospects = clients.filter((c) => c.type === 'prospect').length
    const tauxConversion = clients.length ? Math.round((fideles / clients.length) * 100) : 0

    const cantonMap = {}
    clients.forEach((c) => { if (c.canton) cantonMap[c.canton] = (cantonMap[c.canton] || 0) + 1 })
    const cantonData = Object.entries(cantonMap).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, value]) => ({ name, value }))

    const caParClient = {}
    delivered.forEach((o) => { if (o.client_id) caParClient[o.client_id] = (caParClient[o.client_id] || 0) + (o.ca_total_chf || 0) })
    const clientIds = Object.entries(caParClient).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([id]) => id)
    const { data: top5clients } = await supabase.from('clients').select('id, name').in('id', clientIds)
    const top5 = clientIds.map((id) => ({
      name: top5clients?.find((c) => c.id === id)?.name || '—',
      ca: Math.round(caParClient[id]),
    }))

    // ── Commandes (hors factures directes) ──
    // Factures et pro forma sont des ventes déjà engagées : les compter
    // comme des devis fausserait le taux de validation.
    const estDevis = (o) => o.type !== 'facture' && o.type !== 'proforma'
    const ordersDevis = orders.filter(estDevis)
    const deliveredDevis = delivered.filter(estDevis)
    const statusData = [
      { name: '📤 Envoyé', value: ordersDevis.filter((o) => o.status === 'sent').length, color: t.amber },
      { name: '✅ Validé', value: ordersDevis.filter((o) => o.status === 'validated').length, color: t.green },
      { name: '🚛 Planifié', value: ordersDevis.filter((o) => o.status === 'scheduled').length, color: t.blue },
      { name: '📦 Livré', value: deliveredDevis.length, color: '#8A8F93' },
    ]

    let totalDelai = 0; let countDelai = 0
    ;(devis || []).forEach((d) => {
      if (d.created_at && d.validated_at) {
        totalDelai += (new Date(d.validated_at) - new Date(d.created_at)) / (1000 * 60 * 60 * 24)
        countDelai++
      }
    })
    const delaiMoyen = countDelai ? Math.round(totalDelai / countDelai) : null
    const totalDevis = (devis || []).length
    const devisValides = (devis || []).filter((d) => d.status === 'validated' || d.validated_at).length
    const tauxValidation = totalDevis ? Math.round((devisValides / totalDevis) * 100) : 0

    // ── CA réel ──
    const months12Reel = months12.map((m, idx) => {
      const d = new Date(currentYear, currentMonth - (11 - idx), 1)
      const mo = d.getMonth(); const yr = d.getFullYear()
      return {
        ...m,
        caReel: Math.round(delivered
          .filter((o) => o.ca_reel_chf && o.date_livraison &&
            new Date(o.date_livraison).getMonth() === mo &&
            new Date(o.date_livraison).getFullYear() === yr)
          .reduce((s, o) => s + (o.ca_reel_chf || 0), 0)),
      }
    })

    const prodMapReel = {}
    delivered.forEach((o) => {
      const ratio = o.ca_reel_chf && o.ca_total_chf ? o.ca_reel_chf / o.ca_total_chf : 1
      ;(o.lignes || []).forEach((l) => {
        prodMapReel[catOf(l.produit)] = (prodMapReel[catOf(l.produit)] || 0) + (l.sous_total || 0) * ratio
      })
    })
    const prodDataReel = Object.entries(prodMapReel).map(([name, value]) => ({ name, value: Math.round(value) }))

    /* Les trois montants du carnet, sur les livraisons effectuées.
       Chacun ne compte que sa propre colonne : un repli du réel sur
       l'engagé, comme le faisait l'ancien code, gonflait le réel avec
       des estimations et rendait l'écart illisible. */
    const caEngageTotal = delivered.reduce((s, o) => s + (o.ca_total_chf || 0), 0)
    const caComptaTotal = delivered.reduce((s, o) => s + (o.ca_compta_chf || 0), 0)
    const caReelTotal   = delivered.reduce((s, o) => s + (o.ca_reel_chf || 0), 0)
    const nbSansCompta  = delivered.filter((o) => o.ca_compta_chf == null).length
    const nbSansReel    = delivered.filter((o) => o.ca_reel_chf == null).length

    /* ── Marges par voyage ──────────────────────────────────────
       Un voyage = une livraison effectuée. Le chiffre retenu est le CA
       compta quand il existe, sinon l'engagé : c'est le montant que
       Franck compare à son coût, et se rabattre silencieusement sur
       zéro afficherait des marges négatives fantaisistes. */
    const coutParDevis = Object.fromEntries((couts || []).map((q) => [q.id, q.cout_revient]))
    const coutParCommande = Object.fromEntries((coutsCommandes || []).map((o) => [o.id, o.cout_revient]))
    const nomParClient = Object.fromEntries((noms || []).map((c) => [c.id, c.name]))

    const voyages = delivered
      .map((o) => {
        /* Le coût figé sur la commande prime : c'est le plus récent,
           et une commande saisie à la main n'a pas de devis. */
        const cout = coutParCommande[o.id] || (o.quote_id ? coutParDevis[o.quote_id] : null)
        const ca = o.ca_compta_chf ?? o.ca_total_chf ?? 0
        const coutTotal = cout?.total ?? null
        return {
          id: o.id,
          date: o.date_livraison || o.created_at,
          client: nomParClient[o.client_id] || 'client inconnu',
          produits: (o.lignes || []).map((l) => l.produit).join(', ') || '—',
          tonnage: o.poids_total_t || 0,
          camions: o.nb_camions_total || null,
          fournisseur: cout?.fournisseur_nom || null,
          surCompta: o.ca_compta_chf != null,
          ca,
          cout: coutTotal,
          marge: coutTotal == null ? null : ca - coutTotal,
        }
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date))

    const chiffres = voyages.filter((v) => v.marge != null)
    const margeTotale = chiffres.reduce((s, v) => s + v.marge, 0)
    const coutTotalMarges = chiffres.reduce((s, v) => s + v.cout, 0)
    const caDesChiffres = chiffres.reduce((s, v) => s + v.ca, 0)
    const tonnageChiffre = chiffres.reduce((s, v) => s + v.tonnage, 0)

    /* Montants compta saisis sur des commandes pas encore livrées.
       Ils n'entrent dans aucun total, et rien ne le disait : on croyait
       à un chiffre figé alors qu'il attendait une clôture. */
    const enAttente = (orders || [])
      .filter((o) => o.ca_compta_chf != null && o.status !== 'delivered')
      .map((o) => ({
        id: o.id,
        client: nomParClient[o.client_id] || 'client inconnu',
        statut: o.status,
        type: o.type,
        date: o.date_livraison || o.created_at,
        compta: o.ca_compta_chf,
      }))
      .sort((a, b) => new Date(b.date) - new Date(a.date))
    const comptaEnAttente = enAttente.reduce((s, o) => s + o.compta, 0)

    setStats({
      enAttente, comptaEnAttente,
      voyages, margeTotale, coutTotalMarges, caDesChiffres, tonnageChiffre,
      nbVoyagesChiffres: chiffres.length, nbVoyagesSansCout: voyages.length - chiffres.length,
      months12, months12Reel, prodData, prodDataReel, saisoData,
      prospectsParMois, fideles, prospects, tauxConversion, cantonData, top5,
      statusData, delaiMoyen, tauxValidation, totalDevis,
      caEngageTotal, caComptaTotal, caReelTotal, nbSansCompta, nbSansReel,
      caMonthCurrent: months12[11]?.ca || 0,
      poidsTotalLivre: delivered.reduce((s, o) => s + (o.poids_total_t || 0), 0),
      nbClients: clients.length,
    })
    setLoading(false)
  }

  if (loading) {
    return (
      <>
        <PageHead><div><PageTitle>Statistiques</PageTitle></div></PageHead>
        <Loading rows={4} h="150px" />
      </>
    )
  }
  if (!stats) return <Empty icon="📊" title="Aucune donnée disponible">Les statistiques apparaîtront dès les premières commandes livrées.</Empty>

  const moisPointe = [...stats.saisoData].sort((a, b) => b.thisYear - a.thisYear)[0]

  return (
    <>
      <PageHead>
        <div>
          <PageTitle>Statistiques</PageTitle>
          <PageSub>Chiffre d'affaires, portefeuille clients et cycle de commande</PageSub>
        </div>
      </PageHead>

      <StatGrid style={{ marginBottom: 20 }}>
        <Tile i={0} label="Engagé livré" value={montant(stats.caEngageTotal)} hint="montant des commandes parties" tone="gold" />
        <Tile i={1} label="Compta" value={stats.caComptaTotal > 0 ? montant(stats.caComptaTotal) : '—'}
          hint={stats.comptaEnAttente > 0
            ? `${montant(stats.comptaEnAttente)} en attente de livraison`
            : 'sur les livraisons effectuées'}
          tone="blue" />
        <Tile i={2} label="Réel perçu" value={stats.caReelTotal > 0 ? montant(stats.caReelTotal) : '—'}
          hint={stats.caComptaTotal > 0 && stats.caReelTotal > 0 ? `${montant(stats.caReelTotal - stats.caComptaTotal, { signe: true })} vs compta` : 'ce qui a été gagné'}
          tone="teal" />
        <Tile i={3} label="Tonnage livré" value={`${stats.poidsTotalLivre.toFixed(1)} T`} hint={`${stats.nbClients} clients`} tone="neutral" />
      </StatGrid>

      <ChipBar $scroll style={{ marginBottom: 22 }}>
        {SECTIONS.map((s) => (
          <Chip key={s.key} $on={section === s.key} onClick={() => setSection(s.key)}>{s.label}</Chip>
        ))}
      </ChipBar>

      {/* ══ CHIFFRES ══ */}
      {section === 'chiffres' && stats.enAttente.length > 0 && (
        <Stagger style={{ marginBottom: 16 }}>
          <Panel>
            <ChartHead>Compta en attente — non compté</ChartHead>
            <Muted style={{ fontSize: '0.76rem', margin: '0 0 12px', display: 'block' }}>
              Ces commandes portent un montant compta mais ne sont pas encore marquées
              livrées : elles n'entrent donc dans aucun total ci-dessus. Clôturez-les
              depuis le carnet pour qu'elles soient prises en compte.
            </Muted>
            <TableWrap>
              <Tableau>
                <thead>
                  <tr>
                    <th>Date</th><th>Client</th><th>Type</th><th>Statut</th>
                    <th className="num">Compta</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.enAttente.map((o) => (
                    <tr key={o.id}>
                      <td>{new Date(o.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</td>
                      <td className="fort">{o.client}</td>
                      <td>{ORDER_TYPES[o.type]?.label || o.type || 'Devis'}</td>
                      <td>{ORDER_STATUS[o.statut]?.label || o.statut}</td>
                      <td className="num fort">{montant(o.compta)}</td>
                    </tr>
                  ))}
                </tbody>
              </Tableau>
            </TableWrap>
          </Panel>
        </Stagger>
      )}

      {section === 'chiffres' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <ChartGrid>
            <Stagger $i={0}>
              <Panel>
                <ChartHead>CA réel mensuel — 12 mois</ChartHead>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.months12Reel} barSize={18} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                    <XAxis dataKey="mois" tick={axisTick} axisLine={false} tickLine={false} />
                    <YAxis tick={{ ...axisTick, fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                    <Tooltip formatter={(v) => [montant(v), 'CA réel']} contentStyle={tipStyle} itemStyle={tipItem} labelStyle={tipLabel} cursor={{ fill: t.goldWash }} />
                    <Bar dataKey="caReel" radius={[5, 5, 0, 0]}>
                      {stats.months12Reel.map((_, i) => <Cell key={i} fill={i === 11 ? t.gold : t.blue} opacity={i === 11 ? 1 : 0.6} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            </Stagger>

            <Stagger $i={1}>
              <Panel>
                <ChartHead>Tonnage mensuel — 12 mois (T)</ChartHead>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={stats.months12} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                    <defs>
                      <linearGradient id="tGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={t.blue} stopOpacity={0.32} />
                        <stop offset="100%" stopColor={t.blue} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="mois" tick={axisTick} axisLine={false} tickLine={false} />
                    <YAxis tick={{ ...axisTick, fontSize: 9 }} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v) => [`${v} T`, 'Tonnage']} contentStyle={tipStyle} itemStyle={tipItem} labelStyle={tipLabel} />
                    <Area type="monotone" dataKey="poids" stroke={t.blue} strokeWidth={2} fill="url(#tGrad)" dot={{ r: 3, fill: t.blue }} activeDot={{ r: 5, fill: t.gold }} />
                  </AreaChart>
                </ResponsiveContainer>
              </Panel>
            </Stagger>

            <Stagger $i={2}>
              <Panel>
                <ChartHead>CA réel par produit</ChartHead>
                {stats.prodDataReel.length === 0 ? (
                  <Muted>Aucune donnée</Muted>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={185}>
                      <PieChart>
                        <Pie data={stats.prodDataReel} cx="50%" cy="50%" innerRadius={46} outerRadius={68} dataKey="value" paddingAngle={3}>
                          {stats.prodDataReel.map((e, i) => <Cell key={i} fill={COLORS_PROD[e.name] || t.textFaint} />)}
                        </Pie>
                        <Tooltip formatter={(v) => montant(v)} contentStyle={tipStyle} itemStyle={tipItem} labelStyle={tipLabel} />
                      </PieChart>
                    </ResponsiveContainer>
                    <Legend>
                      {stats.prodDataReel.map((e) => (
                        <span key={e.name}><i style={{ background: COLORS_PROD[e.name] || t.textFaint }} />{e.name}</span>
                      ))}
                    </Legend>
                  </>
                )}
              </Panel>
            </Stagger>

            <Stagger $i={3}>
              <Panel>
                <ChartHead>Mois de pointe · engagé, compta, réel</ChartHead>
                <BigNum $c={t.gold}>{moisPointe?.thisYear ? moisPointe.label : '—'}</BigNum>
                <BigSub>{moisPointe?.thisYear ? `${montant(moisPointe.thisYear)} sur le mois` : 'Pas encore de livraison cette année'}</BigSub>
                <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${t.lineSoft}` }}>
                  <RankRow><RankName>Engagé</RankName><RankVal style={{ color: t.textSoft }}>{montant(stats.caEngageTotal)}</RankVal></RankRow>
                  <RankRow>
                    <RankName>Compta</RankName>
                    <RankVal style={{ color: t.blue }}>{montant(stats.caComptaTotal)}</RankVal>
                  </RankRow>
                  <RankRow><RankName>Réel perçu</RankName><RankVal>{montant(stats.caReelTotal)}</RankVal></RankRow>
                  <RankRow>
                    <RankName>Écart réel / compta</RankName>
                    <RankVal style={{ color: stats.caReelTotal - stats.caComptaTotal >= 0 ? t.green : t.red }}>
                      {montant(stats.caReelTotal - stats.caComptaTotal, { signe: true })}
                    </RankVal>
                  </RankRow>
                </div>
                {(stats.nbSansCompta > 0 || stats.nbSansReel > 0) && (
                  <Muted style={{ fontSize: '0.72rem', marginTop: 10 }}>
                    {stats.nbSansCompta > 0 && `${stats.nbSansCompta} livraison(s) sans montant compta`}
                    {stats.nbSansCompta > 0 && stats.nbSansReel > 0 && ', '}
                    {stats.nbSansReel > 0 && `${stats.nbSansReel} sans réel perçu`}
                    {' '}— antérieures au suivi. Ces totaux sont donc incomplets tant que
                    l'historique n'est pas repris.
                  </Muted>
                )}
              </Panel>
            </Stagger>
          </ChartGrid>

          <Stagger $i={4}>
            <Panel>
              <ChartHead>CA par mois — comparaison N / N-1</ChartHead>
              <ResponsiveContainer width="100%" height={215}>
                <BarChart data={stats.saisoData} barGap={4} barSize={14} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                  <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
                  <YAxis tick={{ ...axisTick, fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                  <Tooltip formatter={(v, n) => [montant(v), n === 'thisYear' ? 'Cette année' : 'Année précédente']} contentStyle={tipStyle} itemStyle={tipItem} labelStyle={tipLabel} cursor={{ fill: t.goldWash }} />
                  <Bar dataKey="thisYear" fill={t.gold} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="lastYear" fill={t.textFaint} radius={[3, 3, 0, 0]} opacity={0.45} />
                </BarChart>
              </ResponsiveContainer>
              <Legend>
                <span><i style={{ background: t.gold }} />Cette année</span>
                <span><i style={{ background: t.textFaint, opacity: 0.5 }} />Année précédente</span>
              </Legend>
            </Panel>
          </Stagger>
        </div>
      )}

      {/* ══ CLIENTS ══ */}
      {section === 'clients' && (
        <ChartGrid>
          <Stagger $i={0}>
            <Panel>
              <ChartHead>Nouveaux clients — 6 mois</ChartHead>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.prospectsParMois} barSize={26} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
                  <YAxis tick={{ ...axisTick, fontSize: 9 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip formatter={(v) => [v, 'Nouveaux clients']} contentStyle={tipStyle} itemStyle={tipItem} labelStyle={tipLabel} cursor={{ fill: t.greenWash }} />
                  <Bar dataKey="count" fill={t.green} radius={[5, 5, 0, 0]} opacity={0.85} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </Stagger>

          <Stagger $i={1}>
            <Panel>
              <ChartHead>Conversion prospect → fidèle</ChartHead>
              <BigNum $c={t.green}>{stats.tauxConversion}%</BigNum>
              <BigSub>Taux de fidélisation</BigSub>
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${t.lineSoft}` }}>
                <RankRow><RankName>🏆 Clients fidèles</RankName><RankVal>{stats.fideles}</RankVal></RankRow>
                <RankRow><RankName>🌱 Prospects</RankName><RankVal style={{ color: t.textSoft }}>{stats.prospects}</RankVal></RankRow>
              </div>
            </Panel>
          </Stagger>

          <Stagger $i={2}>
            <Panel>
              <ChartHead>🏆 Top 5 clients par CA</ChartHead>
              {stats.top5.length === 0 ? <Muted>Aucune donnée</Muted> : stats.top5.map((c, i) => (
                <RankRow key={i}>
                  <RankNum $i={i}>{i + 1}</RankNum>
                  <RankName>{c.name}</RankName>
                  <RankVal>{montant(c.ca)}</RankVal>
                </RankRow>
              ))}
            </Panel>
          </Stagger>

          <Stagger $i={3}>
            <Panel>
              <ChartHead>Répartition par canton / région</ChartHead>
              {stats.cantonData.length === 0 ? <Muted>Aucune donnée</Muted> : (
                <ResponsiveContainer width="100%" height={Math.max(180, stats.cantonData.length * 30)}>
                  <BarChart data={stats.cantonData} layout="vertical" barSize={14} margin={{ top: 0, right: 22, left: 10, bottom: 0 }}>
                    <XAxis type="number" tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: t.text }} axisLine={false} tickLine={false} width={82} />
                    <Tooltip formatter={(v) => [v, 'Clients']} contentStyle={tipStyle} itemStyle={tipItem} labelStyle={tipLabel} cursor={{ fill: t.greenWash }} />
                    <Bar dataKey="value" fill={t.green} radius={[0, 5, 5, 0]} opacity={0.8} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Panel>
          </Stagger>
        </ChartGrid>
      )}

      {/* ══ COMMANDES ══ */}
      {/* ══ MARGES ══ */}
      {section === 'marges' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <StatGrid>
            <Tile i={0} label="CA des voyages chiffrés" value={montant(stats.caDesChiffres)}
              hint={`${stats.nbVoyagesChiffres} voyage(s)`} tone="blue" />
            <Tile i={1} label="Coût de revient" value={montant(stats.coutTotalMarges)}
              hint="marchandise, approvisionnement, livraison" tone="neutral" />
            <Tile i={2} label="Marge" value={montant(stats.margeTotale, { signe: true })}
              hint={stats.caDesChiffres > 0
                ? `${(stats.margeTotale / stats.caDesChiffres * 100).toFixed(1)} % du chiffre`
                : 'aucun voyage chiffré'}
              tone={stats.margeTotale >= 0 ? 'green' : 'red'} />
            <Tile i={3} label="Marge à la tonne"
              value={stats.tonnageChiffre > 0 ? montant(stats.margeTotale / stats.tonnageChiffre) : '—'}
              hint={`sur ${stats.tonnageChiffre.toFixed(1)} T`} tone="teal" />
          </StatGrid>

          <Stagger $i={0}>
            <Panel>
              <ChartHead>Récapitulatif par voyage</ChartHead>

              {stats.nbVoyagesSansCout > 0 && (
                <Muted style={{ fontSize: '0.76rem', margin: '0 0 12px', display: 'block' }}>
                  {stats.nbVoyagesSansCout} livraison(s) sans coût de revient figé — elles
                  apparaissent dans le tableau mais ne comptent dans aucun total. Le coût se
                  fige depuis la fiche du devis, en bas de page.
                </Muted>
              )}

              {stats.voyages.length === 0 ? (
                <Muted style={{ textAlign: 'center', margin: '20px 0' }}>Aucune livraison effectuée.</Muted>
              ) : (
                <TableWrap>
                  <Tableau>
                    <thead>
                      <tr>
                        <th>Date</th><th>Client</th><th>Marchandise</th>
                        <th className="num">Tonnage</th>
                        <th>Fournisseur</th>
                        <th className="num">CA</th>
                        <th className="num">Coût</th>
                        <th className="num">Marge</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.voyages.map((v) => (
                        <tr key={v.id}>
                          <td>{new Date(v.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</td>
                          <td className="fort">{v.client}</td>
                          <td>{v.produits}{v.camions ? ` · ${v.camions} camion${v.camions > 1 ? 's' : ''}` : ''}</td>
                          <td className="num">{v.tonnage ? `${v.tonnage} T` : '—'}</td>
                          <td>{v.fournisseur || '—'}</td>
                          <td className="num fort">{montant(v.ca)}{v.surCompta ? '' : ' *'}</td>
                          <td className="num">{v.cout == null ? '—' : montant(v.cout)}</td>
                          <td className="num">
                            {v.marge == null
                              ? <Muted style={{ fontSize: '0.74rem' }}>non chiffré</Muted>
                              : <Gain $positive={v.marge >= 0}>{montant(v.marge, { signe: true })}</Gain>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Tableau>
                </TableWrap>
              )}

              <Muted style={{ fontSize: '0.72rem', marginTop: 10, display: 'block' }}>
                Le CA retenu est le montant compta ; un astérisque signale une livraison
                sans montant compta saisi, où l'engagé du devis a été repris.
              </Muted>
            </Panel>
          </Stagger>
        </div>
      )}

      {section === 'commandes' && (
        <ChartGrid>
          <Stagger $i={0}>
            <Panel>
              <ChartHead>Répartition des commandes par statut</ChartHead>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={stats.statusData} cx="50%" cy="50%" innerRadius={48} outerRadius={72} dataKey="value" paddingAngle={3}>
                    {stats.statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={tipStyle} itemStyle={tipItem} labelStyle={tipLabel} />
                </PieChart>
              </ResponsiveContainer>
              <Legend>
                {stats.statusData.map((e) => (
                  <span key={e.name}><i style={{ background: e.color }} />{e.name} · {e.value}</span>
                ))}
              </Legend>
            </Panel>
          </Stagger>

          <Stagger $i={1}>
            <Panel>
              <ChartHead>Taux de validation des devis</ChartHead>
              <BigNum $c={t.gold}>{stats.tauxValidation}%</BigNum>
              <BigSub>{stats.totalDevis} demande(s) de devis au total</BigSub>
            </Panel>
          </Stagger>

          <Stagger $i={2}>
            <Panel>
              <ChartHead>Délai moyen devis → validation</ChartHead>
              {stats.delaiMoyen !== null ? (
                <>
                  <BigNum $c={t.blue}>{stats.delaiMoyen} j</BigNum>
                  <BigSub>Entre la demande et la validation client</BigSub>
                </>
              ) : (
                <Muted style={{ textAlign: 'center', marginTop: 24 }}>Pas assez de données</Muted>
              )}
            </Panel>
          </Stagger>
        </ChartGrid>
      )}
    </>
  )
}
